import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { compressImage } from "@/lib/compressImage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import { useAuth } from "@/contexts/AuthContext";
import { MapPin, Clock, Activity as ActivityIcon, Camera, ExternalLink, Trash2, Pencil, PlusCircle } from "lucide-react";
import StravaConnectButton from "@/components/StravaConnectButton";
import PoweredByStrava from "@/components/PoweredByStrava";

const TYPES = [
  { v: "run", label: "Running" },
  { v: "walk", label: "Walking" },
  { v: "cycle", label: "Cycling" },
  { v: "other", label: "Other" },
];

const MIN_ACTIVITY_DATE = "2026-06-23";  // Must match backend MIN_ACTIVITY_DATE

function todayStr() {
  // IST/local date — never UTC. Otherwise an athlete at 1 AM IST gets
  // yesterday's UTC date pre-filled when logging today's run.
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 10);
}

function paceLabel(p) {
  if (!p) return null;
  const m = Math.floor(p);
  const s = Math.round((p - m) * 60);
  return `${m}:${String(s).padStart(2, "0")} /km`;
}

// Convert decimal minutes (backend storage) → {h, m, s} for display
function minutesToHMS(min) {
  if (min == null || min === "" || isNaN(min)) return { h: 0, m: 0, s: 0 };
  const totalSec = Math.round(Number(min) * 60);
  return {
    h: Math.floor(totalSec / 3600),
    m: Math.floor((totalSec % 3600) / 60),
    s: totalSec % 60,
  };
}

// Format decimal minutes → "HH:MM:SS"
function formatHMS(min) {
  const { h, m, s } = minutesToHMS(min);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

// Combine h/m/s string inputs into decimal minutes for backend
function hmsToMinutes(h, m, s) {
  const H = Number(h) || 0;
  const M = Number(m) || 0;
  const S = Number(s) || 0;
  if (H === 0 && M === 0 && S === 0) return null;
  return +(H * 60 + M + S / 60).toFixed(4);
}

export default function ActivityLog() {
  const { user, refresh } = useAuth();
  const [items, setItems] = useState([]);
  const [editingId, setEditingId] = useState(null);   // activity_id being edited, or null
  const [f, setF] = useState({
    date: todayStr(), activity_type: "run", distance_km: "",
    dur_h: "", dur_m: "", dur_s: "",
    notes: "", strava_url: "", photo_base64: "",
  });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/activities").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const stravaConnected = !!user?.strava_connected;

  const resetForm = () => {
    setEditingId(null);
    setF({ date: todayStr(), activity_type: "run", distance_km: "",
           dur_h: "", dur_m: "", dur_s: "",
           notes: "", strava_url: "", photo_base64: "" });
  };

  const startEdit = (a) => {
    const hms = minutesToHMS(a.duration_min);
    setEditingId(a.activity_id);
    setF({
      date: (a.date || "").slice(0, 10),
      activity_type: a.activity_type || "run",
      distance_km: a.distance_km != null ? String(a.distance_km) : "",
      dur_h: hms.h ? String(hms.h) : "",
      dur_m: hms.m ? String(hms.m) : "",
      dur_s: hms.s ? String(hms.s) : "",
      notes: a.notes || "",
      strava_url: a.strava_url || "",
      photo_base64: a.photo_base64 || "",
    });
    // Scroll to top so user sees the composer
    window.scrollTo({ top: 200, behavior: "smooth" });
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) { toast.error("Max 20MB"); return; }
    try {
      const { dataUrl } = await compressImage(file);
      setF((s) => ({ ...s, photo_base64: dataUrl }));
    } catch {
      toast.error("Could not read photo");
    }
  };

  const submit = async () => {
    if (!f.date) { toast.error("Date required"); return; }
    if (f.date < MIN_ACTIVITY_DATE) {
      toast.error(`Activities before ${MIN_ACTIVITY_DATE} are not allowed.`);
      return;
    }
    const duration_min = hmsToMinutes(f.dur_h, f.dur_m, f.dur_s);
    if (!f.distance_km && !duration_min) {
      toast.error("Enter at least distance or duration");
      return;
    }
    setBusy(true);
    try {
      const body = {
        date: f.date,
        activity_type: f.activity_type,
        distance_km: Number(f.distance_km) || 0,
        duration_min,
        notes: f.notes,
        strava_url: f.strava_url || null,
        photo_base64: f.photo_base64 || null,
      };
      if (editingId) {
        await api.put(`/activities/${editingId}`, body);
        toast.success("Activity updated");
      } else {
        await api.post("/activities", body);
        toast.success("Activity logged");
      }
      resetForm();
      await Promise.all([load(), refresh()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this activity?")) return;
    try {
      await api.delete(`/activities/${id}`);
      await Promise.all([load(), refresh()]);
    } catch { toast.error("Delete failed"); }
  };

  const totals = useMemo(() => {
    const last30 = items.filter((a) => {
      const d = new Date(a.date);
      const cutoff = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
      return d >= cutoff;
    });
    const sum = last30.reduce((s, a) => s + (a.distance_km || 0), 0);
    const days = new Set(last30.map((a) => a.date)).size;
    return { km30: sum.toFixed(1), days30: days, total: items.length };
  }, [items]);

  if (!user) return null;

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10" data-testid="activity-page">
      <SEO title="Daily Activity Log" path="/activity" description="Log your daily running, walking and cycling — manually or with a Strava activity URL. OSCR tracks your kilometres, pace and streaks." />
      <p className="font-mono text-[11px] tracking-[0.3em] text-orange-500 uppercase">ACTIVITY LOG</p>
      <h1 className="font-display text-4xl sm:text-6xl text-white mt-2">My Activity</h1>
      <p className="text-zinc-400 mt-3 max-w-2xl">
        Log any daily activity — running, walking, cycling. Enter manually, or paste a Strava
        activity link as proof. Everything you log adds to your total kilometres and feeds the
        OSCR leaderboard.
      </p>

      {/* Strava integration row */}
      <div className="mt-6 hairline bg-zinc-900 p-4 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <p className="font-mono text-[10px] tracking-[0.3em] text-orange-500 uppercase">STRAVA AUTO-SYNC</p>
          <p className="text-sm text-zinc-300 mt-1">
            {stravaConnected
              ? "Connected. Every upload to Strava lands here automatically. Tap SYNC NOW to pull the latest."
              : "Connect Strava once to auto-sync every run/walk/ride. Or keep logging manually below — both work."}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <StravaConnectButton
            connected={stravaConnected}
            username={user?.strava_username}
            onChange={async () => { await refresh(); await load(); }}
          />
          <PoweredByStrava size="sm" />
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-3 gap-3 mt-8">
        <Tile label="KM · LAST 30 DAYS" value={totals.km30} />
        <Tile label="ACTIVE DAYS · 30D" value={totals.days30} />
        <Tile label="TOTAL ENTRIES" value={totals.total} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mt-8">
        {/* Composer */}
        <div className="lg:col-span-2">
          <div className="hairline bg-zinc-900 p-5 space-y-3 sticky top-24" data-testid="activity-composer">
            <div className="flex items-center justify-between gap-2">
              <h3 className="font-display text-xl text-white tracking-wider flex items-center gap-2">
                <PlusCircle size={18} className="text-orange-500" /> {editingId ? "EDIT ACTIVITY" : "LOG NEW ACTIVITY"}
              </h3>
              {editingId && (
                <button
                  onClick={resetForm}
                  data-testid="cancel-edit-btn"
                  className="font-mono text-[10px] tracking-[0.25em] text-zinc-400 hover:text-white transition uppercase"
                >
                  CANCEL ✕
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="font-display tracking-widest text-xs text-zinc-400">DATE</Label>
                <Input data-testid="act-date" type="date" min={MIN_ACTIVITY_DATE} value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })}
                  className="mt-2 rounded-none bg-zinc-950 border-zinc-800 text-white focus-visible:ring-orange-600" />
                <p className="font-mono text-[9px] text-zinc-500 mt-1 tracking-widest uppercase">
                  No dates before {MIN_ACTIVITY_DATE}
                </p>
              </div>
              <div>
                <Label className="font-display tracking-widest text-xs text-zinc-400">TYPE</Label>
                <Select value={f.activity_type} onValueChange={(v) => setF({ ...f, activity_type: v })}>
                  <SelectTrigger data-testid="act-type" className="mt-2 rounded-none bg-zinc-950 border-zinc-800 text-white focus:ring-orange-600">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800 text-white rounded-none">
                    {TYPES.map((t) => (
                      <SelectItem key={t.v} value={t.v} className="rounded-none focus:bg-orange-600 focus:text-white">{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="font-display tracking-widest text-xs text-zinc-400">DISTANCE (KM)</Label>
                <Input data-testid="act-distance" type="number" step="0.01" value={f.distance_km}
                  onChange={(e) => setF({ ...f, distance_km: e.target.value })}
                  placeholder="e.g. 5.2"
                  className="mt-2 rounded-none bg-zinc-950 border-zinc-800 text-white focus-visible:ring-orange-600" />
              </div>
              <div>
                <Label className="font-display tracking-widest text-xs text-zinc-400">DURATION (HH:MM:SS)</Label>
                <div className="mt-2 grid grid-cols-3 gap-1">
                  <Input
                    data-testid="act-duration-h"
                    type="number"
                    min="0"
                    max="99"
                    value={f.dur_h}
                    onChange={(e) => setF({ ...f, dur_h: e.target.value })}
                    placeholder="HH"
                    className="rounded-none bg-zinc-950 border-zinc-800 text-white focus-visible:ring-orange-600 text-center font-mono"
                  />
                  <Input
                    data-testid="act-duration-m"
                    type="number"
                    min="0"
                    max="59"
                    value={f.dur_m}
                    onChange={(e) => setF({ ...f, dur_m: e.target.value })}
                    placeholder="MM"
                    className="rounded-none bg-zinc-950 border-zinc-800 text-white focus-visible:ring-orange-600 text-center font-mono"
                  />
                  <Input
                    data-testid="act-duration-s"
                    type="number"
                    min="0"
                    max="59"
                    value={f.dur_s}
                    onChange={(e) => setF({ ...f, dur_s: e.target.value })}
                    placeholder="SS"
                    className="rounded-none bg-zinc-950 border-zinc-800 text-white focus-visible:ring-orange-600 text-center font-mono"
                  />
                </div>
              </div>
            </div>
            <div>
              <Label className="font-display tracking-widest text-xs text-zinc-400">NOTES</Label>
              <Textarea data-testid="act-notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })}
                placeholder="Felt great. Light easy run. Tempo session. Walked with family..."
                className="mt-2 rounded-none bg-zinc-950 border-zinc-800 text-white focus-visible:ring-orange-600 min-h-[70px]" />
            </div>
            <div>
              <Label className="font-display tracking-widest text-xs text-zinc-400 flex items-center gap-1">
                <ExternalLink size={11} /> STRAVA ACTIVITY URL (optional)
              </Label>
              <Input data-testid="act-strava" value={f.strava_url} onChange={(e) => setF({ ...f, strava_url: e.target.value })}
                placeholder="https://www.strava.com/activities/..."
                className="mt-2 rounded-none bg-zinc-950 border-zinc-800 text-white focus-visible:ring-orange-600" />
              <p className="font-mono text-[10px] text-zinc-500 mt-1 tracking-widest uppercase">Paste link from your Strava activity</p>
            </div>
            <div>
              <Label className="font-display tracking-widest text-xs text-zinc-400 flex items-center gap-1">
                <Camera size={11} /> PHOTO (optional, max 20MB)
              </Label>
              <input data-testid="act-photo" type="file" accept="image/*" onChange={onFile}
                className="mt-2 block w-full text-sm text-zinc-300 file:mr-3 file:py-2 file:px-4 file:rounded-none file:border-0 file:bg-zinc-800 file:text-white file:font-display file:tracking-wider hover:file:bg-zinc-700" />
              {f.photo_base64 && <img src={f.photo_base64} alt="" className="mt-2 max-h-32 hairline" />}
            </div>
            <Button data-testid="act-submit" onClick={submit} disabled={busy}
              className="w-full rounded-none bg-orange-600 hover:bg-orange-500 font-display tracking-wider py-6">
              {busy ? "SAVING…" : editingId ? "UPDATE ACTIVITY" : "LOG ACTIVITY"}
            </Button>
          </div>
        </div>

        {/* List */}
        <div className="lg:col-span-3">
          <h3 className="font-display text-xl text-white tracking-wider mb-4">RECENT</h3>
          {items.length === 0 ? (
            <div className="hairline bg-zinc-900 p-8 text-center text-zinc-500 font-mono text-sm">
              No activities yet. Log your first run on the left.
            </div>
          ) : (
            <div className="space-y-2">
              {items.map((a) => {
                const t = TYPES.find((x) => x.v === a.activity_type)?.label || a.activity_type;
                return (
                  <article key={a.activity_id} className="hairline bg-zinc-900 p-4" data-testid={`activity-${a.activity_id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-3">
                          <span className="font-mono text-[10px] tracking-[0.25em] text-orange-500 uppercase">
                            {new Date(a.date).toDateString()}
                          </span>
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 hairline bg-zinc-950 font-mono text-[10px] uppercase tracking-widest text-zinc-300">
                            <ActivityIcon size={10} /> {t}
                          </span>
                        </div>
                        <div className="mt-2 flex flex-wrap items-baseline gap-x-5 gap-y-1">
                          <span className="font-display text-2xl text-white">{a.distance_km || 0} <span className="text-sm text-zinc-500">KM</span></span>
                          {a.duration_min != null && (
                            <span className="font-mono text-xs text-zinc-300 flex items-center gap-1" data-testid={`duration-${a.activity_id}`}>
                              <Clock size={12} className="text-orange-500" /> {formatHMS(a.duration_min)}
                            </span>
                          )}
                          {a.pace_min_per_km && (
                            <span className="font-mono text-xs text-orange-500">
                              {paceLabel(a.pace_min_per_km)}
                            </span>
                          )}
                        </div>
                        {a.notes && <p className="text-sm text-zinc-400 mt-2">{a.notes}</p>}
                        {a.strava_url && (
                          <a href={a.strava_url} target="_blank" rel="noopener noreferrer"
                            className="mt-2 inline-flex items-center gap-1 text-xs text-orange-500 hover:underline break-all">
                            <ExternalLink size={11} /> View on Strava
                          </a>
                        )}
                        {a.photo_base64 && <img src={a.photo_base64} alt="" className="mt-3 max-h-40 hairline" />}
                      </div>
                      <div className="flex flex-col gap-2 shrink-0">
                        <button
                          onClick={() => startEdit(a)}
                          className="text-zinc-500 hover:text-orange-500 transition"
                          title="Edit"
                          data-testid={`edit-activity-${a.activity_id}`}
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          onClick={() => del(a.activity_id)}
                          className="text-zinc-600 hover:text-red-500 transition"
                          title="Delete"
                          data-testid={`delete-activity-${a.activity_id}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Tile({ label, value }) {
  return (
    <div className="hairline bg-zinc-900 p-4">
      <div className="font-display text-2xl sm:text-3xl text-white">{value}</div>
      <div className="font-mono text-[10px] text-zinc-500 tracking-[0.25em] mt-1">{label}</div>
    </div>
  );
}
